import React, { useCallback } from 'react';
import { Button, Card, Col, Modal, Row } from 'antd';
import { Link } from 'react-router-dom';

import { getFullName } from 'utils/personUtils';
import { getLocation } from 'utils/locationUtils';
import BaseLabel from 'components/wrappers/BaseLabel';
import BaseAlert from 'components/wrappers/BaseAlert';
import { getRecruiterStatusName } from 'utils/recruiterUtils';
import { getAccountTypeLabel, getProfileTypeLabel } from 'utils/accountUtils';
import { BOAccountViewModel } from 'api/adminApiClient';
import adminApiClient from 'api/adminApiClient';
import ClientStatusLabel from 'components/ClientStatusLabel';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { ApiErrorMessage } from 'constants/ErrorMessages';

import styles from './RecruiterCard.module.less';

type Props = {
  recruiter: BOAccountViewModel;
  onDeactivateSuccess?: (userId: string) => void;
};

export default function RecruiterCard(props: Props) {
  const { recruiter, onDeactivateSuccess } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const {
    userId,
    memberKey,
    firstName,
    lastName,
    companyName,
    email,
    contactPhoneNumber,
    city,
    state,
    status,
    isDeleted,
    boUserType,
    profileType,
    countryName,
    internationalCityName,
    internationalStateName,
    clientStatus,
  } = recruiter;

  const handleDeactivateClick = useCallback(() => {
    if (!userId) return;

    Modal.confirm({
      title:
        "If you deactivate this member, they won't be able to sign in unless you reactivate them. Do you want to proceed?",
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        try {
          await adminApiClient.deactivateUser(userId);
          BaseAlert.success('Member has been deactivated successfully.');
          onDeactivateSuccess?.(userId);
        } catch (error: unknown) {
          const message =
            error && typeof error === 'object' && 'message' in error
              ? String((error as { message: unknown }).message)
              : ApiErrorMessage;
          BaseAlert.error(message);
        }
      },
    });
  }, [userId, onDeactivateSuccess]);

  const getRecruiterLocation = () => {
    return getLocation(
      countryName,
      city || internationalCityName,
      state || internationalStateName,
    );
  };

  const recruiterLocation = getRecruiterLocation();
  const recruiterFullName = getFullName(firstName, lastName);
  const recruiterStatus = isDeleted
    ? 'Deactivated'
    : getRecruiterStatusName(status);

  return (
    <Card
      className="card-item size-sm stretch-card"
      data-id={`member-${userId}`}
    >
      <div className="card-inner h-centered">
        <div className="inner-text-box">
          <div data-id="member-name">
            <Link className="card-title" to={`/member/${memberKey}`}>
              {recruiterFullName}
            </Link>
          </div>

          <div className="card-link">{companyName}</div>
        </div>
      </div>

      <Row gutter={[20, 10]} className="titled-sheet">
        {boUserType && (
          <Col xs={24} md={12} data-id="account-type">
            <div className="title">Account type: </div>
            <div>{getAccountTypeLabel(boUserType)}</div>
          </Col>
        )}

        {profileType && (
          <Col xs={24} md={12} data-id="profile-type">
            <div className="title">Profile type: </div>
            <div>{getProfileTypeLabel(profileType)}</div>
          </Col>
        )}

        {memberKey && (
          <Col xs={24} md={12} data-id="member-id">
            <div className="title">Member ID: </div>
            <div>{memberKey}</div>
          </Col>
        )}

        {recruiterLocation && (
          <Col xs={24} md={12} data-id="billing-location">
            <div className="title">Billing location: </div>
            <div>{recruiterLocation}</div>
          </Col>
        )}

        {contactPhoneNumber && (
          <Col xs={24} md={12} data-id="contact-number">
            <div className="title">Contact number: </div>
            <div>{contactPhoneNumber}</div>
          </Col>
        )}

        {email && (
          <Col xs={24} md={12} data-id="email">
            <div className="title">Email: </div>
            <div>{email}</div>
          </Col>
        )}

        <Col xs={24} className={styles.col} data-id="status-label">
          {isAdminRole && <ClientStatusLabel clientStatus={clientStatus} />}
          <BaseLabel status={recruiterStatus} />
        </Col>

        {isAdminRole && userId && (
          <Col xs={24} className={styles.col}>
            <Button
              onClick={handleDeactivateClick}
              disabled={!!isDeleted}
            >
              Deactivate
            </Button>
          </Col>
        )}
      </Row>
    </Card>
  );
}
